import * as React from 'react';
import { useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Button, Form, Modal } from 'antd';
import ProjectInternalTeamsField, {
	confirmLosingProjectAccess,
	CurrentUserForAccess,
	getInternalTeamIdsFromValues,
	getInternalTeamsInitialValues,
	willLoseProjectAccess,
} from '../project_internal_teams_field/ProjectInternalTeamsField';
import OWButton from '../ow_button/OWButton';
import { withUserType } from '../withUserType';
import { runWithMessageFeedback } from '../../utils/MessageFeedbackUtils';
import { ROLE_TYPES } from '../../utils/DataConstants';
import {
	projectsRestCrudThunksForBuyer,
	projectsRestCrudThunksForSupplier,
} from '../../thunks/projects_thunks';

// Change which internal teams can see a project, without a trip through the full
// project edit form — the ask from the Pet Valu feedback was that access be both
// visible and easy to change from the project itself.
//
// Projects have no partial-update endpoint (PUT /project/projects takes the whole
// entity), so the save re-sends the fetched record with only supplierFacilityIds
// swapped. Rebuilding a payload here instead would risk clearing fields the record
// already holds (projectTemplateDetails above all), which is exactly the trap
// ProjectEditForm documents.

interface ProjectAccessModalProps {
	// The project record as fetched, re-sent whole on save.
	project: any;
	onCancel: () => void;
	// Called with the updated record, and whether the user just removed their own team
	// (so can no longer read it); not called when the save fails.
	onSuccess?: (project: any, lostAccess: boolean) => void;
	// Injected by connect / withUserType.
	currentUser?: CurrentUserForAccess;
	updateProject?: (project: any) => Promise<any>;
	userType?: string;
}

const ProjectAccessModal: React.FC<ProjectAccessModalProps> = ({
	project,
	onCancel,
	onSuccess,
	currentUser,
	updateProject,
}) => {
	const [form] = Form.useForm();
	const [saving, setSaving] = useState(false);
	// `saving` state can't guard the second click on its own: validateFields is async,
	// so a double-click lands before setSaving(true) has rendered and both saves PUT
	// the project. A ref flips synchronously, closing that window.
	const savingRef = useRef(false);

	const save = () => {
		if (savingRef.current) {
			return;
		}
		savingRef.current = true;
		form
			.validateFields()
			.then((values) => {
				const internalTeamIds = getInternalTeamIdsFromValues(values);
				const lostAccess = willLoseProjectAccess(currentUser, internalTeamIds);
				return (lostAccess ? confirmLosingProjectAccess() : Promise.resolve(true)).then(
					(confirmed) => {
						if (!confirmed) {
							return;
						}
						setSaving(true);
						return (
							runWithMessageFeedback(
								updateProject({
									...project,
									// null from the helper means "don't narrow"; an explicit empty array is
									// what clears the list, since undefined is dropped by JSON.stringify and
									// the backend would keep the project's existing teams.
									supplierFacilityIds: internalTeamIds === null ? [] : internalTeamIds,
								}),
								{
									key: 'project-access-op',
									loading: 'Updating access…',
									success: 'Project access updated',
									failure: 'Failed to update project access',
								}
							)
								// runWithMessageFeedback reports the error on the toast and swallows it,
								// resolving undefined — so a missing record means the save failed and the
								// modal stays open with the user's picks intact.
								.then((record) => {
									setSaving(false);
									if (record && onSuccess) {
										onSuccess(record, lostAccess);
									}
								})
						);
					}
				);
			})
			// Release on validation failure too, or the user could never retry.
			.then(
				() => {
					savingRef.current = false;
				},
				() => {
					savingRef.current = false;
				}
			);
	};

	return (
		<Modal
			visible
			title="Who can access this project"
			onCancel={onCancel}
			destroyOnClose
			// Custom footer rather than okText/onOk: a network write has to go through
			// OWButton, which blocks the duplicate click and disables itself while the
			// request is in flight (the repo's network-submit convention).
			footer={[
				<Button key="cancel" onClick={onCancel} disabled={saving}>
					Cancel
				</Button>,
				<OWButton key="save" type="primary" loading={saving} onClick={save}>
					Save
				</OWButton>,
			]}
		>
			<Form form={form} layout="vertical" initialValues={getInternalTeamsInitialValues(project)}>
				<ProjectInternalTeamsField form={form} />
			</Form>
		</Modal>
	);
};

const mapDispatchToProps = (dispatch, ownProps) => ({
	updateProject: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? projectsRestCrudThunksForSupplier.update(entity)
				: projectsRestCrudThunksForBuyer.update(entity)
		),
});

const mapStateToProps = (state) => ({
	currentUser: state.session.currentUser,
});

export default withUserType(connect(mapStateToProps, mapDispatchToProps)(ProjectAccessModal));
